import {
  Button,
  Distribute,
  Spacer,
  TextButton,
  TextInput,
} from '@loomhq/lens';
import classNames from 'classnames';
import React, { useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { ENTER, ESCAPE } from 'constants/keyCodes';
import { addCommentCreator } from 'creators/commentsGraphQl';
import { getAnonUserName } from 'utilities/auth';

import { selectHasAuthenticatedAccessScope } from 'selectors/scopes';
import { selectUserIsLogged } from 'selectors/user';

import AutoresizeTextarea from 'components/ui-components/autoresize-textarea';

import styles from './styles.module.less';

const ONELINE_MAX_ROWS = 4;
const MULTILINE_MIN_ROWS = 2;

const CommentInputGroup = props => {
  const dispatch = useDispatch();

  const anonUserName = getAnonUserName();

  const [comment, setComment] = useState('');
  const [anonName, setAnonName] = useState(anonUserName);
  const [elementToFocus, setFocus] = useState(null);

  const {
    addReplyId,
    allowOneLine,
    creationMethod,
    drawCancel,
    grabFocus,
    expandOnEntry,
    hideNameIfPresent,
    inputSize,
    onCancel,
    onSubmit,
    placeholder,
    submitButtonText,
  } = props;

  const commentInput = useRef();
  const nameInput = useRef();

  useEffect(() => {
    if (grabFocus && commentInput.current) {
      (elementToFocus || commentInput.current).focus({ preventScroll: true });
    }
  }, [grabFocus, elementToFocus]);

  function anonNameToUse() {
    return anonName ? anonName : getAnonUserName();
  }

  const onAddComment = () => {
    dispatch(
      addCommentCreator(comment, addReplyId, anonNameToUse(), creationMethod)
    );

    commentInput.current.blur();
    setComment('');
    onSubmit && onSubmit();
  };

  const isLoggedIn = useSelector(selectUserIsLogged);
  const hasAuthenticatedAccess = useSelector(selectHasAuthenticatedAccessScope);
  const shouldBeAnon = !isLoggedIn || !hasAuthenticatedAccess;

  const direction = allowOneLine
    ? ['column', 'column', 'row', 'row']
    : 'column';

  function validCommentInput() {
    return Boolean(comment.length && comment.trim().length);
  }

  function canSubmitComment() {
    const needAnonName = shouldBeAnon && !anonNameToUse().trim().length;

    return validCommentInput() && !needAnonName;
  }

  const showAllFields = !expandOnEntry || validCommentInput();
  const showActionButtons = showAllFields;
  const showAnonNameField =
    shouldBeAnon && !(hideNameIfPresent && anonUserName) && showAllFields;

  return (
    <Distribute
      className={styles.commentGroupContainer}
      direction={direction}
      alignment="end"
      gap="small"
      onKeyDown={e => {
        if (canSubmitComment() && e.keyCode === ENTER && !e.shiftKey) {
          onAddComment();
          e.preventDefault();
        } else if (e.keyCode === ESCAPE) {
          onCancel && onCancel();
        }
      }}
    >
      <div
        className={classNames(styles.commentWrapper, {
          [styles.oneLineCommentWrapper]: allowOneLine && !shouldBeAnon,
          [styles.oneLineGuestCommentWrapper]: allowOneLine && shouldBeAnon,
        })}
      >
        <AutoresizeTextarea
          data-testid="comment-entry-field"
          size={inputSize}
          className={styles.textArea}
          value={comment}
          placeholder={placeholder}
          onChange={ev => setComment(ev.target.value)}
          textRef={commentInput}
          onFocus={() => setFocus(commentInput.current)}
          minRows={!allowOneLine ? MULTILINE_MIN_ROWS : undefined}
          maxRows={allowOneLine ? ONELINE_MAX_ROWS : undefined}
        />
      </div>
      {showAnonNameField && (
        <div
          className={classNames(styles.nameWrapper, {
            [styles.oneLineNameWrapper]: allowOneLine,
          })}
        >
          <TextInput
            data-testid="comment-anon-name-field"
            size={inputSize}
            placeholder="Your name"
            onChange={ev => setAnonName(ev.target.value)}
            ref={nameInput}
            value={anonName}
            onFocus={() => setFocus(nameInput.current)}
          ></TextInput>
        </div>
      )}
      {showActionButtons && (
        <div
          className={classNames(styles.actionButtonGroup, {
            [styles.allowReverse]: allowOneLine,
          })}
        >
          {drawCancel && (
            <TextButton size={inputSize} onClick={onCancel}>
              Cancel
            </TextButton>
          )}
          <Spacer left="small" />
          <Button
            size={inputSize}
            variant="primary"
            isDisabled={!canSubmitComment()}
            onClick={onAddComment}
          >
            {submitButtonText}
          </Button>
        </div>
      )}
      {!showActionButtons && <Spacer top="36px" />}
    </Distribute>
  );
};

export default CommentInputGroup;
